import * as React from 'react';

import { BadgeModel } from 'models';

import {
  UitkBadge,
  UitkBadgeTheme,
  UitkDealGenericBadge,
  UitkDealMemberBadge,
  UitkFamilyFriendlyBadge,
  UitkSavedBadge,
  UitkSponsoredBadge,
  UitkSupplierPromoBadge,
  UitkViewedBadge,
  UitkVipBadge,
} from 'uitk-react-badge';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkMark, UitkMarkSize } from 'uitk-react-mark';

import { Theme } from '__generated__/api';

interface BadgeGeneratorProps {
  badge: BadgeModel;
  badgeClasses?: string;
  dataStid?: string;
}

export const BadgeGenerator: React.FunctionComponent<BadgeGeneratorProps> = ({ badge, badgeClasses, dataStid }) => {
  const DecorativeElement = getDecorativeElement(badge);

  switch (badge.theme) {
    case Theme.SPONSORED:
      return <UitkSponsoredBadge className={badgeClasses} text={badge.text} data-stid={dataStid} />;
    case Theme.VIP_ACCESS:
      return <UitkVipBadge className={badgeClasses} text={badge.text} data-stid={dataStid} />;
    case Theme.DEAL_ADD_ON:
      return <UitkDealGenericBadge children={DecorativeElement} className={badgeClasses} data-stid={dataStid} text={badge.text} />;
    case Theme.DEAL_BUNDLED:
      return <UitkDealGenericBadge children={DecorativeElement} className={badgeClasses} data-stid={dataStid} text={badge.text} />;
    case Theme.DEAL_GENERIC:
      return <UitkDealGenericBadge children={DecorativeElement} className={badgeClasses} data-stid={dataStid} text={badge.text} />;
    case Theme.DEAL_MEMBER:
      return <UitkDealMemberBadge children={DecorativeElement} className={badgeClasses} data-stid={dataStid} text={badge.text} />;
    case Theme.FAMILY_FRIENDLY:
      return <UitkFamilyFriendlyBadge children={DecorativeElement} className={badgeClasses} data-stid={dataStid} text={badge.text} />;
    case Theme.SAVED:
      return (
        <UitkSavedBadge
          children={DecorativeElement}
          className={badgeClasses}
          text={badge.text}
          theme={UitkBadgeTheme[badge.theme]}
          data-stid={dataStid}
        />
      );
    case Theme.SUPPLIER_PROMO:
      return (
        <UitkSupplierPromoBadge
          children={DecorativeElement}
          className={badgeClasses}
          text={badge.text}
          theme={UitkBadgeTheme[badge.theme]}
          data-stid={dataStid}
        />
      );
    case Theme.VIEWED:
      return <UitkViewedBadge children={DecorativeElement} className={badgeClasses} text={badge.text} data-stid={dataStid} />;
    default:
      return (
        <UitkBadge className={badgeClasses} text={badge.text} theme={UitkBadgeTheme[badge.theme]} data-stid={dataStid}>
          {badge?.icon && <UitkIcon name={badge.icon} />}
        </UitkBadge>
      );
  }
};

const getDecorativeElement = (badge: BadgeModel): JSX.Element | null => {
  const icon = badge?.icon ? <UitkIcon className="uitk-badge-icon" name={badge.icon} size={UitkIconSize.EXTRA_SMALL} /> : null;
  const mark = badge?.mark ? (
    <UitkMark className="uitk-badge-mark" name={badge.mark} size={UitkMarkSize.EXTRA_SMALL} description="" />
  ) : null;

  return icon ? icon : mark;
};

BadgeGenerator.displayName = 'BadgeGenerator';
